import React, { useState } from 'react';
import { User } from '../types';
import { GoogleLoginButton } from './GoogleAuthModal';

interface OnboardingScreenProps {
  onComplete: (user?: User) => void;
}

export const OnboardingScreen: React.FC<OnboardingScreenProps> = ({ onComplete }) => {
  const [step, setStep] = useState(0);

  const slides = [
    {
      id: 1,
      // Bright, happy kitchen scene
      image: "https://images.unsplash.com/photo-1507048331197-7d4ac70811cf?auto=format&fit=crop&w=1000&q=80",
      title: "Hallo!",
      subtitle: "Goeindag! bij superMarct",
      description: "De slimme hulp voor je boodschappen. Scan recepten uit boeken, maak gedeelde lijstjes en kook lekkerder.",
      accent: "bg-emerald-500"
    },
    {
      id: 2,
      // Vibrant market/shopping scene
      image: "https://images.unsplash.com/photo-1583258292688-d0213dc5a3a8?auto=format&fit=crop&w=1000&q=80",
      title: "Gezamenlijke Lijst",
      subtitle: "Nooit meer dubbele boodschappen",
      description: "Nodig je partner of huisgenoten uit. Wat jij thuis opmaakt, zien zij direct in de supermarkt op hun telefoon.",
      accent: "bg-blue-500"
    },
    {
      id: 3,
      // Fresh, organized food (Healthy bowl top-down)
      image: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&w=1000&q=80",
      title: "Jouw Database",
      subtitle: "Al je favorieten op één plek",
      description: "Bouw je persoonlijke kookboek. Plan je weekmenu en zet ingrediënten met één klik op je boodschappenlijst.",
      accent: "bg-amber-500"
    }
  ];

  const nextStep = () => {
      if (step < slides.length - 1) {
          setStep(step + 1);
      }
  };

  const currentSlide = slides[step];

  return (
    <div className="fixed inset-0 z-[200] flex flex-col bg-black">
        
        {/* Full Screen Image Layer */}
        <div className="absolute inset-0 z-0">
             {slides.map((slide, idx) => (
                 <div 
                    key={slide.id}
                    className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${step === idx ? 'opacity-100' : 'opacity-0'}`}
                 >
                     <img 
                        src={slide.image} 
                        alt={slide.title} 
                        className="w-full h-full object-cover"
                     />
                     {/* Gradient Overlay for text readability/smooth transition to sheet */}
                     <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/90"></div>
                 </div>
             ))}
        </div>

        {/* Top Header */}
        <div className="absolute top-0 left-0 w-full p-6 pt-10 z-10 flex justify-between items-center">
            <div className="flex gap-2">
                {slides.map((_, idx) => (
                    <div 
                        key={idx} 
                        className={`h-1.5 rounded-full transition-all duration-300 drop-shadow-md ${idx === step ? 'w-8 bg-white' : 'w-2 bg-white/40'}`}
                    ></div>
                ))}
            </div>
            
            {/* Skip Button - Always visible for steps 0 and 1 */}
            <div className={`transition-opacity duration-300 ${step < 2 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                 <button 
                    onClick={() => setStep(2)}
                    className="px-4 py-2 rounded-full bg-white/20 backdrop-blur-md border border-white/20 text-xs font-bold text-white shadow-lg hover:bg-white/30 active:scale-95 transition-all flex items-center gap-1"
                >
                    <span>Overslaan</span>
                    <span className="material-symbols-outlined text-[16px]">chevron_right</span>
                </button>
            </div>
        </div>

        {/* Bottom Sheet Content */}
        <div className="absolute bottom-0 left-0 w-full z-20">
            <div className="mx-4 mb-6 p-1 bg-white/20 dark:bg-black/40 backdrop-blur-xl rounded-[32px] border border-white/20 shadow-2xl overflow-hidden">
                <div className="bg-white dark:bg-[#15241b] rounded-[28px] p-6 pb-8 flex flex-col items-center text-center">
                    
                    {/* Content Text */}
                    <div className="mb-8 min-h-[140px] flex flex-col justify-center animate-in slide-in-from-bottom-4 fade-in duration-500" key={`text-${step}`}>
                        <span className={`inline-block mx-auto px-3 py-1 rounded-full text-[10px] font-bold text-white uppercase tracking-wider mb-3 ${currentSlide.accent}`}>
                            {currentSlide.title}
                        </span>
                        <h1 className="text-2xl font-extrabold text-gray-900 dark:text-white mb-3 leading-tight">
                            {currentSlide.subtitle}
                        </h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed max-w-[280px] mx-auto">
                            {currentSlide.description}
                        </p>
                    </div>

                    {/* Action Area */}
                    <div className="w-full space-y-3">
                        {step < 2 ? (
                            <button 
                                onClick={nextStep}
                                className="w-full py-4 rounded-2xl bg-gray-900 dark:bg-white text-white dark:text-black font-bold text-base shadow-lg active:scale-[0.98] transition-transform flex items-center justify-center gap-2 group"
                            >
                                Volgende
                                <span className="material-symbols-outlined text-lg transition-transform group-hover:translate-x-1">arrow_forward</span>
                            </button>
                        ) : (
                            <div className="animate-in fade-in zoom-in-95 duration-300 w-full space-y-3">
                                {/* Modern Google Login Button */}
                                <GoogleLoginButton 
                                    onLogin={(user) => onComplete(user)} 
                                    label="Inloggen met Google" 
                                />

                                <div className="relative py-2">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-gray-100 dark:border-white/5"></div>
                                    </div>
                                    <div className="relative flex justify-center text-[10px] uppercase font-bold text-gray-400">
                                        <span className="bg-white dark:bg-[#15241b] px-2">of</span>
                                    </div>
                                </div>

                                <button 
                                    onClick={() => onComplete()}
                                    className="w-full py-3.5 rounded-2xl border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 font-bold hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-sm cursor-pointer"
                                >
                                    Gebruik lokaal (Gast)
                                </button>
                                
                                <p className="text-[10px] text-gray-400 pt-2">
                                   Door verder te gaan accepteer je de voorwaarden.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    </div>
  );
};